<script>
    import { onDestroy, onMount } from 'svelte';
    import { evidenceStore } from '../stores/evidenceStore';
    import { storyStore } from '../stores/storyStore.js';
    import { authStore } from '../stores/authStore.js';
    import { generateResponse, testAIConfig, testAnthropicConnection, ERROR_TYPES } from '../services/aiService';
    import { initializeChat as initializeChatSession, getChatHistory, addMessage as addChatMessage, addWelcomeMessage } from '../services/chatService';
    import { logAgentActivity, getAgentActivityLogsByChatThread } from '../services/agentActivityService';
    import { writable, get } from 'svelte/store';
    import { slide } from 'svelte/transition';
    import { AI_CONFIG } from '../../backend/config/aiConfig';
    import { chartAgentInstructions } from '../stores/agentInstructionsStore';
    export let key = 0; // Add this prop
  
    // Add debug mode for production troubleshooting
    const DEBUG_MODE = window.location.hostname === 'datacreative.app';
    
    // Constants
    const SENDER_USER = 'user';
    const SENDER_AI = 'ai';
    let currentSuggestions = [];
    
    // Custom instructions for this specific agent
    const AGENT_INSTRUCTIONS = AI_CONFIG.agentInstructions.chartAnalysis;
    
    // Quick actions
    const QUICK_ACTIONS = [
        { id: 'identify-parts', text: 'Help me understand the parts of this graph' },
        { id: 'find-patterns', text: 'Help me find patterns in the data' },
        { id: 'connect-argument', text: 'Help me connect this to my argument' }
    ];

    // Initialize state variables
    let currentInstructions;
    let editableInstructions;
    let authState = { user: null };
    let storyState = { _id: null };
    let evidenceState = { currentEvidence: null };
    let messages = [];
    let userInput = '';
    let isLoading = false;
    let chatBox; // Reference to main chat container
    let loadInterval; // Define loadInterval
    let currentContext = null;
    let isTyping = false;
    let showQuickActions = false; // Start with suggestions visible
    let currentChat = null;
    let isInitialized = false;
    let isInitializing = false;
    let hasInitializedChat = false; // Add new flag to track if chat has been initialized
    let errorMessage = null; // Add error message variable
    let isHistoryVisible = false; // Track if history is visible
    let showCustomInput = false;

    // Connection status
    let connectionStatus = {
        checking: false,
        connected: false,
        lastChecked: null,
        error: null,
        errorType: null
    };

    let connectionCheckInterval = null;

    // Track consecutive connection failures
    let connectionFailures = 0;

    // Subscribe to evidence store to get current evidence
    let currentEvidence = null;
    evidenceStore.subscribe(store => {
        currentEvidence = store.currentEvidence;
    });

    // Clean up subscription
    onDestroy(() => {
        // No need to clean up evidence subscription as it's handled by the store
    });

    // Function to test API connection
    async function checkApiConnection() {
        if (connectionStatus.checking) return;
        
        // Don't attempt connection check if we don't have authentication
        if (!authState?.user?.uid) {
            console.log('Skipping API connection check - user not authenticated yet');
            return false;
        }
        
        connectionStatus = {
            ...connectionStatus,
            checking: true
        };
        
        try {
            if (DEBUG_MODE) {
                console.log('DEBUG: Checking Anthropic API connection...');
            }
            
            const result = await testAnthropicConnection();
            
            connectionStatus = {
                checking: false,
                connected: result.success,
                lastChecked: new Date(),
                error: result.success ? null : result.error,
                errorType: result.success ? null : result.errorType
            };
            
            if (DEBUG_MODE) {
                console.log('DEBUG: Connection status updated:', connectionStatus);
            }
            
            // Only show firewall error if we've tried multiple times
            if (!result.success && result.errorType === ERROR_TYPES.FIREWALL) {
                // Only set error message if we've had multiple consecutive failures
                if (connectionFailures >= 2) {
                    errorMessage = 'The AI service is unavailable because it appears to be blocked by your school network or firewall. Please contact your teacher or IT administrator for assistance.';
                } else {
                    // Increment failure counter but don't show error yet
                    connectionFailures++;
                    console.log(`Connection failure ${connectionFailures}/2 - waiting for additional confirmation`);
                }
            } else if (result.success) {
                // Reset failure counter on success
                connectionFailures = 0;
            }
            
            return result.success;
        } catch (error) {
            console.error('Error checking API connection:', error);
            
            let errorType = ERROR_TYPES.UNKNOWN;
            
            // Handle 404 error specially (endpoint not found)
            if (error.message && error.message.includes('404')) {
                console.warn('Connection test endpoint not found - server may need to be restarted');
                // Don't show error to user in this case
                connectionStatus = {
                    checking: false,
                    connected: true, // Assume connected for better UX
                    lastChecked: new Date(),
                    error: null,
                    errorType: null
                };
                return true;
            }
            
            connectionStatus = {
                checking: false,
                connected: false,
                lastChecked: new Date(),
                error: error.message,
                errorType
            };
            
            return false;
        }
    }

    // Set up periodic connection checking
    function setupConnectionChecking() {
        // Delay initial check to ensure app is fully loaded
        setTimeout(() => {
            checkApiConnection();
            
            // Set up interval for rechecking (every 2 minutes)
            connectionCheckInterval = setInterval(() => {
                checkApiConnection();
            }, 2 * 60 * 1000); // Check every 2 minutes
        }, 3000); // Wait 3 seconds before first check
    }

    // Clean up interval on component destroy
    onDestroy(() => {
        if (connectionCheckInterval) {
            clearInterval(connectionCheckInterval);
        }
    });

    // And subscribe to the stores
    chartAgentInstructions.subscribe(value => {
        currentInstructions = value;
        editableInstructions = value;
    });

    const unsubscribeAuth = authStore.subscribe(state => {
        authState = state || { user: null };
        console.log('Auth state updated:', {
            hasUser: !!authState?.user,
            hasUserId: !!authState?.user?.uid,
            userId: authState?.user?.uid
        });
        initializeChatIfReady();
    });

    // Update the reactive statement to prevent multiple initializations
    $: {
        key; // Create dependency on key
        if (!isInitializing && !hasInitializedChat) {
            // Initialize chat if we have the required data
            if (storyState?._id && evidenceState?.currentEvidence?._id && authState?.user?.uid) {
                initializeChatIfReady();
            }
        }
    }

    // Update initializeChatIfReady to prevent multiple initialization attempts
    async function initializeChatIfReady() {
        // Prevent multiple initialization attempts
        if (isInitializing || hasInitializedChat) {
            console.log('[Chat] Initialization already in progress or completed');
            return;
        }

        const hasStoryId = !!storyState?._id;
        const hasEvidenceId = !!evidenceState?.currentEvidence?._id;
        const hasUserId = !!authState?.user?.uid;
        const hasExistingMessages = messages.length > 0;

        console.log('[Chat] Checking initialization conditions:', {
            hasStoryId,
            hasEvidenceId,
            hasUserId,
            hasExistingMessages,
            isInitializing,
            hasInitializedChat
        });

        if (hasStoryId && hasEvidenceId && hasUserId && !hasExistingMessages) {
            isInitializing = true;
            let didSucceed = false;
            try {
                await initializeChat();
                didSucceed = true;
            } catch (err) {
                console.error('[Chat] initializeChatIfReady error:', err);
            } finally {
                isInitializing = false;
                // Failsafe: If backend succeeded or messages were set, always exit initializing
                if (didSucceed || messages.length > 0) {
                    console.log('[Chat] Failsafe: Forcing UI out of initializing state.');
                    isInitializing = false;
                    hasInitializedChat = true;
                }
            }
            // Failsafe: If still stuck after 5 seconds, force exit initializing
            setTimeout(() => {
                if (isInitializing) {
                    console.warn('[Chat] Failsafe timeout: Forcing UI out of initializing after 5s.');
                    isInitializing = false;
                    if (messages.length > 0) hasInitializedChat = true;
                }
            }, 5000);
        }
    }

    // Update initializeChat to use the new addWelcomeMessage function
    async function initializeChat() {
        try {
            console.log('Starting chat initialization...');
            
            // Set loading state
            isInitializing = true;

            // Get current evidence and story IDs
            const currentEvidence = get(evidenceStore);
            const currentStory = get(storyStore);

            // Debug the evidence and story state to see what we're working with
            console.log('Current evidence state:', currentEvidence);
            console.log('Current story state:', currentStory);

            if (DEBUG_MODE) {
                console.log('DEBUG: Evidence store:', currentEvidence);
                console.log('DEBUG: Story store:', currentStory);
                console.log('DEBUG: Auth state:', authState);
            }

            if (!currentEvidence?.currentEvidence?._id || !currentStory?._id || !authState?.user?.uid) {
                console.log('Missing required data for chat initialization');
                if (DEBUG_MODE) {
                    console.error('DEBUG: Missing required data for chat initialization');
                    console.error('DEBUG: Evidence ID:', currentEvidence?.currentEvidence?._id);
                    console.error('DEBUG: Story ID:', currentStory?._id);
                    console.error('DEBUG: User ID:', authState?.user?.uid);
                }
                
                // Set error message to inform the user about the missing data
                errorMessage = 'Cannot initialize chat. Missing required data - please make sure you have selected evidence and are logged in.';
                isInitializing = false;
                return;
            }

            // Validate evidence type - ensure it's a chart for chart analysis
            if (currentEvidence?.currentEvidence?.type !== 'chart' && 
                currentEvidence?.currentEvidence?.type !== 'image' && 
                !currentEvidence?.currentEvidence?.isChart) {
                console.warn('Evidence is not a chart type:', currentEvidence?.currentEvidence?.type);
                // This is not necessarily an error condition, but helpful to log
            }

            const evidenceId = currentEvidence.currentEvidence._id;
            const storyId = currentStory._id;
            const userId = authState.user.uid;

            console.log('Initializing chat with IDs:', { 
                userId, 
                storyId: typeof storyId === 'object' ? storyId.toString() : storyId, 
                evidenceId: typeof evidenceId === 'object' ? evidenceId.toString() : evidenceId 
            });

            // Initialize chat session - ensure we're passing strings if needed
            try {
                currentChat = await initializeChatSession({
                    userId,
                    storyId: typeof storyId === 'object' ? storyId.toString() : storyId,
                    chatType: 'chart-analysis',
                    evidenceId: typeof evidenceId === 'object' ? evidenceId.toString() : evidenceId
                });
                
                if (!currentChat?._id) {
                    throw new Error('Failed to initialize chat: No chat ID returned');
                }
                
                console.log('Chat session initialized:', currentChat);
            } catch (chatError) {
                console.error('Error initializing chat session:', chatError);
                errorMessage = 'Failed to initialize chat session. Please try again later.';
                isInitializing = false;
                return;
            }

            // Get chat history
            let history;
            try {
                history = await getChatHistory({
                    userId,
                    storyId: typeof storyId === 'object' ? storyId.toString() : storyId,
                    chatType: 'chart-analysis',
                    evidenceId: typeof evidenceId === 'object' ? evidenceId.toString() : evidenceId
                });
            } catch (historyError) {
                console.error('Error getting chat history, continuing with empty history:', historyError);
                // Non-fatal error, continue with empty history
                history = [];
            }

            // Ensure history is an array
            const messageHistory = Array.isArray(history) ? history : [];
            console.log(`Retrieved ${messageHistory.length} messages from chat history`);

            // Process chat history
            if (messageHistory.length > 0) {
                // Chat history exists, populate the UI with the messages
                messages = messageHistory.map(msg => ({
                    id: msg._id || generateMessageId(),
                    content: msg.content,
                    sender: msg.sender,
                    timestamp: msg.timestamp || new Date(),
                    suggestions: msg.suggestions || []
                }));
                
                // Set the current suggestions from the last AI message
                const lastAiMessage = messageHistory
                    .filter(msg => msg.sender === 'ai')
                    .pop();
                    
                if (lastAiMessage && lastAiMessage.suggestions) {
                    currentSuggestions = lastAiMessage.suggestions;
                }
                
                console.log('Chat history loaded successfully');
            } else {
                // No chat history, create welcome message
                console.log('No chat history found, creating welcome message');
                
                const welcomeContent = "Hi there! I'm here to help you analyze this chart. What would you like to know about it?";
                const welcomeSuggestions = [
                    "Connect graph to data story",
                    "Help me understand the graph"
                ];
                
                // Create welcome message for UI
                const welcomeMessage = {
                    id: generateMessageId(),
                    content: welcomeContent,
                    sender: 'ai',
                    timestamp: new Date(),
                    suggestions: welcomeSuggestions
                };
                
                // Add to UI
                messages = [welcomeMessage];
                currentSuggestions = welcomeSuggestions;
                
                // Save to database
                if (currentChat?._id) {
                    try {
                        await addChatMessage(currentChat._id, {
                            content: welcomeContent,
                            sender: 'ai',
                            timestamp: new Date(),
                            suggestions: welcomeSuggestions
                        });
                        
                        // Log AI message activity
                        await logAgentActivity({
                            chatThreadId: currentChat._id,
                            userId,
                            storyId,
                            evidenceId,
                            activityType: 'message_received',
                            details: {
                                messageContent: welcomeContent,
                                messageType: 'welcome'
                            }
                        });
                        
                        console.log('Welcome message saved to database');
                    } catch (error) {
                        console.error('Error saving welcome message:', error);
                        // Non-fatal error, the UI already has the welcome message
                    }
                }

                // Wait for DOM update then attach button handlers
                setTimeout(attachButtonHandlers, 100);
            }

            // Set initialization flag
            hasInitializedChat = true;
            errorMessage = null; // Clear any previous error messages
            console.log('Chat initialization completed successfully');

        } catch (error) {
            console.error('Error initializing chat:', error);
            // Show detailed error information
            console.error('Error details:', error.stack, error.message);
            // Show error to user
            errorMessage = error.message.includes('chat') 
                ? 'Failed to initialize chat. Please try refreshing the page.' 
                : 'Error connecting to the server. Please check your connection and try again.';
            // Reset the initialization flag so we can try again
            hasInitializedChat = false;
        } finally {
            isInitializing = false;
        }
    }

    // Update store subscriptions to be more reactive
    const unsubscribeStory = storyStore.subscribe(state => {
        storyState = state || { _id: null };
        console.log('Story state updated:', {
            hasStoryId: !!storyState?._id,
            storyId: storyState?._id
        });
        // Only initialize if we have all required data and haven't initialized yet
        if (!hasInitializedChat && storyState?._id && evidenceState?.currentEvidence?._id && authState?.user?.uid) {
            initializeChatIfReady();
        }
    });

    // Update the evidence store subscription to properly handle deselection
    const unsubscribeEvidence = evidenceStore.subscribe(state => {
        const previousEvidenceId = evidenceState?.currentEvidence?._id;
        evidenceState = state || { currentEvidence: null };
        
        console.log('Evidence state updated:', {
            hasEvidence: !!evidenceState?.currentEvidence,
            hasEvidenceId: !!evidenceState?.currentEvidence?._id,
            evidenceId: evidenceState?.currentEvidence?._id,
            previousEvidenceId
        });

        // Only reset if evidence is deselected or changed
        if (!evidenceState?.currentEvidence || 
            (previousEvidenceId && previousEvidenceId !== evidenceState?.currentEvidence?._id)) {
            messages = [];
            currentChat = null;
            currentSuggestions = [];
            hasInitializedChat = false;
            console.log('Chat state reset due to evidence change or deselection');
        } else if (evidenceState?.currentEvidence && !hasInitializedChat && storyState?._id && authState?.user?.uid) {
            // Only initialize if we have all required data
            initializeChatIfReady();
        }
    });

    // Message handling functions
    function generateMessageId() {
        return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    function addMessage(text, sender, suggestions = []) {
        // Ensure text is a string and not undefined
        const messageText = typeof text === 'string' ? text : 'Invalid message format';
                       
        const newMessage = {
            id: generateMessageId(),
            content: messageText, // Use content as the primary message field
            text: messageText,    // Keep text for backward compatibility
            sender,
            suggestions,
            timestamp: new Date()
        };
        
        messages = [...messages, newMessage];
    
        // If this is an AI message, also update currentSuggestions for display
        if (sender === SENDER_AI) {
            currentSuggestions = suggestions;
        }
    
        // Ensure chat scrolls to bottom
        setTimeout(scrollToBottom, 100);
    }

    // Settings state
    let showSettings = false;
    
    function toggleSettings() {
        showSettings = !showSettings;
    }

    function saveInstructions() {
        chartAgentInstructions.set(editableInstructions);
        messages = []; 
        showSettings = false;
    }

    function resetInstructions() {
        chartAgentInstructions.reset();
        messages = []; 
        showSettings = false;
    }

    // Add this constant near the top with other constants
    const MAX_HISTORY_MESSAGES = 5; // Limit history to last 5 messages to avoid token limits

    // Update the sendMessage function's prompt construction
    async function sendMessage(userMessage = null) {
        const messageText = userMessage || userInput.trim();
        if (!messageText) return;

        // Check if we have the required data for sending a message
        if (!authState?.user?.uid || !storyState?._id || !evidenceState?.currentEvidence?._id) {
            addMessage('Sorry, I cannot respond right now. The application is still loading or missing required data. Please try refreshing the page.', 'ai');
            console.error('Cannot send message - missing required data:', {
                hasUser: !!authState?.user?.uid,
                hasStoryId: !!storyState?._id,
                hasEvidenceId: !!evidenceState?.currentEvidence?._id
            });
            return;
        }

        // Auto-initialize chat if needed
        if (!hasInitializedChat && !isInitializing) {
            isInitializing = true;
            try {
                await initializeChat();
            } catch (error) {
                console.error('Failed to initialize chat:', error);
                addMessage('Sorry, I encountered an error initializing the chat. Please try refreshing the page.', 'ai');
                isInitializing = false;
                return;
            }
            isInitializing = false;
        }

        // Check connection with automatic retry
        let isConnected = true;
        
        // If connection is unknown or was previously failed, check again
        if (!connectionStatus.lastChecked || 
            (connectionStatus.lastChecked && !connectionStatus.connected)) {
            
            console.log('Checking connection before sending message...');
            isConnected = await checkApiConnection();
            
            // If first check fails, try once more after a short delay
            if (!isConnected) {
                console.log('First connection check failed, retrying after delay...');
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
                isConnected = await checkApiConnection();
            }
            
            if (!isConnected) {
                addMessage('Sorry, I cannot respond right now. The AI service appears to be unavailable. Please try again in a moment.', 'ai');
                return;
            }
        }

        // Store the message before clearing input values
        const messageToSend = messageText;
        
        // Reset UI state
        showCustomInput = false;
        userInput = '';
        isLoading = true;
        
        // Create user message object
        const userMessageObj = {
            id: generateMessageId(),
            content: messageToSend,
            sender: 'user',
            timestamp: new Date()
        };
        
        // Add to UI
        addMessage(messageToSend, 'user');

        // Save user message to database if we have a chat session
        if (currentChat?._id) {
            try {
                await addChatMessage(currentChat._id, {
                    content: messageToSend,
                    sender: 'user',
                    timestamp: new Date()
                });

                // Log user message activity
                await logAgentActivity({
                    chatThreadId: currentChat._id,
                    userId: authState.user.uid,
                    storyId: storyState._id,
                    evidenceId: evidenceState.currentEvidence._id,
                    activityType: 'message_sent',
                    details: {
                        messageContent: messageToSend
                    }
                });
            } catch (error) {
                console.error('Error saving user message:', error);
                if (DEBUG_MODE) {
                    console.error('DEBUG: Error saving user message:', error);
                    console.error('DEBUG: Error stack:', error.stack);
                }
            }
        }

        // Add temporary loading message
        const loadingMessage = 'Thinking...';
        addMessage(loadingMessage, 'ai');

        try {
            // Define context first
            const context = {
                currentEvidence: evidenceState.currentEvidence,
                instructions: currentInstructions,
                conversationHistory: messages
                    .slice(-MAX_HISTORY_MESSAGES)
                    .map(msg => ({
                        sender: msg.sender === 'user' ? 'User' : 'Assistant',
                        content: msg.content,
                        timestamp: new Date(msg.timestamp).toLocaleString()
                    }))
            };

            if (DEBUG_MODE) {
                console.log('DEBUG: Sending message with context:', context);
                console.log('DEBUG: Current instructions:', currentInstructions);
            }

            const prompt = `
                ${currentInstructions}
                
                Context:
                - Evidence Description: ${context.currentEvidence?.description_easy || 'No description'}
                - Evidence Image: ${context.currentEvidence?.s3URL || 'No image link'}
                - Evidence Visual elements: ${context.currentEvidence?.visual_elements|| 'No visual elements'}
                - Evidence Statistics: ${context.currentEvidence?.relevant_statistical_mentions|| 'No relevant statistics'}
                - Recent Conversation History (${new Date().toLocaleString()}):
                    ${context.conversationHistory
                        .map(msg => `[${msg.timestamp}] ${msg.sender}: ${msg.content}`)
                        .join('\n                    ')}
                
                Current User Message: ${messageToSend}

                IMPORTANT: Format your response as JSON. Do NOT include numbers, emojis, or bullet points in the suggestions. Keep suggestions clear and concise.
                {
                  "message": "Your analysis of the chart",
                  "suggestions": [
                    "Tell me more about this trend",
                    "Compare with other regions",
                    "Explain the implications"
                  ]
                }
            `;

            if (DEBUG_MODE) {
                console.log('DEBUG: Prompt being sent to AI:', prompt);
            }

            const response = await generateResponse(prompt, context);
            
            if (DEBUG_MODE) {
                console.log('DEBUG: AI response received:', response);
                console.log('DEBUG: Response suggestions:', response.suggestions);
            }
            
            // Remove loading message
            messages = messages.filter(m => m.text !== loadingMessage);
            
            // Process response text and create buttons if there are suggestions
            let formattedText = response.text;
            
            // Remove the buttonHtml generation since we'll render suggestions separately
            formattedText = response.text;
            
            // Create AI message object
            const aiMessageObj = {
                id: generateMessageId(),
                content: formattedText,
                text: formattedText,
                sender: 'ai',
                timestamp: new Date(),
                suggestions: response.suggestions || []
            };
            
            // Add to UI
            addMessage(formattedText, 'ai', response.suggestions || []);
            
            // Ensure custom input is hidden
            showCustomInput = false;
            
            // Wait for DOM update then attach button handlers
            setTimeout(attachButtonHandlers, 100);
            
            // Save AI message to database if we have a chat session
            if (currentChat?._id) {
                try {
                    await addChatMessage(currentChat._id, {
                        content: formattedText,
                        sender: 'ai',
                        timestamp: new Date(),
                        suggestions: response.suggestions || []
                    });

                    // Log AI message activity
                    await logAgentActivity({
                        chatThreadId: currentChat._id,
                        userId: authState.user.uid,
                        storyId: storyState._id,
                        evidenceId: evidenceState.currentEvidence._id,
                        activityType: 'message_received',
                        details: {
                            messageContent: response.text,
                            suggestions: response.suggestions || []
                        }
                    });
                } catch (error) {
                    console.error('Error saving AI message:', error);
                    if (DEBUG_MODE) {
                        console.error('DEBUG: Error saving AI message:', error);
                        console.error('DEBUG: Error stack:', error.stack);
                    }
                }
            }
        } catch (error) {
            console.error('Error getting AI response:', error);
            if (DEBUG_MODE) {
                console.error('DEBUG: Error getting AI response:', error);
                console.error('DEBUG: Error stack:', error.stack);
                console.error('DEBUG: Error name:', error.name);
                console.error('DEBUG: Error message:', error.message);
            }
            
            const errorMessage = {
                id: generateMessageId(),
                content: 'Sorry, I encountered an error. Please try again.',
                sender: 'ai',
                timestamp: new Date()
            };
            messages = [...messages, errorMessage];
            if (currentChat && currentChat._id) {
                try {
                    await addChatMessage(currentChat._id, errorMessage);
                } catch (saveError) {
                    console.error('Error saving error message:', saveError);
                    if (DEBUG_MODE) {
                        console.error('DEBUG: Error saving error message:', saveError);
                        console.error('DEBUG: Error stack:', saveError.stack);
                    }
                }
            }
        } finally {
            isLoading = false;
        }
    }

    function loader(uniqueId) {
      const messageIndex = messages.length - 1; // Get the index of the last AI message
      messages[messageIndex].text = " "; // Set loading message
      loadInterval = setInterval(() => {
        messages[messageIndex].text += '.';
        if (messages[messageIndex].text === '....') {
          messages[messageIndex].text = ' ';
        }
      }, 300);
    }
  
    function typeText(messageIndex, text) {
        let index = 0;
        const interval = setInterval(() => {
            if (index < text.length) {
                messages[messageIndex].text += text.charAt(index);
                messages = messages; // Trigger Svelte reactivity
                index++;
            } else {
                clearInterval(interval);
            }
        }, 20);
    }

    // Add quick action handler
    async function handleQuickAction(action) {
        if (isLoading) return; // Prevent multiple requests
        userInput = action.text;
        await sendMessage();
    }

    function handleSuggestionClick(option) {
    const messageText = typeof option === 'string' ? option : option.text || 'Invalid option';
    sendMessage(messageText);
}

    // Add auto-scroll function
    function scrollToBottom() {
        if (chatBox) {
            setTimeout(() => {
                chatBox.scrollTo({
                    top: chatBox.scrollHeight,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }

    // Function to scroll to a specific message by ID
    function scrollToMessage(messageId) {
        setTimeout(() => {
            if (chatBox) {
                const messageElement = document.getElementById(messageId);
                if (messageElement) {
                    messageElement.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'center' 
                    });
                    
                    // Add a brief highlight animation to the message
                    messageElement.classList.add('highlight-message');
                    setTimeout(() => {
                        messageElement.classList.remove('highlight-message');
                    }, 2000);
                }
            }
        }, 100);
    }

    // Cleanup on component destruction
    onDestroy(() => {
        unsubscribeStory();
        unsubscribeEvidence();
        unsubscribeAuth();
        if (loadInterval) clearInterval(loadInterval);
        
        // Log chat closure if we have a chat session
        if (currentChat) {
            logAgentActivity({
                chatThreadId: currentChat._id,
                userId: authState?.user?.uid,
                storyId: storyState?._id,
                evidenceId: evidenceState?.currentEvidence?._id,
                activityType: 'chat_closed',
                details: {
                    messageCount: messages.length
                }
            }).catch(error => {
                console.error('Error logging chat closure:', error);
            });
        }
    });

    onMount(async () => {
        isInitialized = true;
        try {
            if (DEBUG_MODE) {
                console.log('DEBUG: Testing AI config on mount');
            }
            
            // Test general config
            const config = await testAIConfig();
            console.log('AI Config loaded:', config);
            
            // Wait a moment before checking connection to ensure all stores are loaded
            setTimeout(() => {
                // Only check connection if we have authentication
                if (authState?.user?.uid) {
                    setupConnectionChecking();
                } else {
                    // Set up a watcher that will check connection once auth is loaded
                    const unsubscribeAuth = authStore.subscribe(state => {
                        if (state?.user?.uid && !connectionCheckInterval) {
                            setupConnectionChecking();
                            unsubscribeAuth();
                        }
                    });
                }
            }, 1000);
        } catch (error) {
            console.error('Failed to load AI config:', error);
            if (DEBUG_MODE) {
                console.error('DEBUG: Failed to load AI config:', error);
                console.error('DEBUG: Error stack:', error.stack);
            }
        }
    });

    // Listen for askAgent events
    onMount(() => {
        window.addEventListener('askAgent', handleAskAgent);
        return () => {
            window.removeEventListener('askAgent', handleAskAgent);
        };
    });

    function handleAskAgent(event) {
        try {
            const { question, context } = event.detail || {};
            
            // Validate required data
            if (!question) {
                console.error('Missing question in askAgent event');
                return;
            }
            
            // Store context but validate its structure
            if (!context) {
                console.warn('Missing or invalid context in askAgent event');
            }
            
            currentContext = context || {}; // Store context but ensure it's an object
            
            // Add the question to messages with unique ID
            messages = [...messages, {
                id: generateMessageId(),
                text: question,
                content: question,
                sender: 'user'
            }];
            
            // Proceed to handle the response with whatever context we have
            handleResponse(question, currentContext);
        } catch (error) {
            console.error('Error handling askAgent event:', error);
            // Add error message to chat
            messages = [...messages, {
                id: generateMessageId(),
                content: "Sorry, I couldn't process that request due to an error. Please try again.",
                sender: 'ai'
            }];
        }
    }

    async function handleResponse(question, context) {
    const loadingId = generateMessageId();
    messages = [...messages, { 
        id: loadingId,
        text: " ",
        sender: 'ai'
    }];
    
    try {
        const { region, description, imageData } = context || {};

        // Add validation for region and imageData
        if (!region || !imageData) {
            console.error('Missing required data for chart analysis:', { hasRegion: !!region, hasImageData: !!imageData });
            
            // Use a simplified prompt when region data is unavailable
            const simplePrompt = `
                Question about chart: ${question}
                
                Chart Description: ${description || 'No description available'}
                
                Please analyze this chart and answer the question. If specific details are needed that aren't available, please explain what information would help you answer better.
                
                Format your response with suggestion buttons at the end to help the user explore further.
            `;
            
            const response = await generateResponse(simplePrompt, { description });
            
            if (DEBUG_MODE) {
                console.log('DEBUG: Response received:', response);
                console.log('DEBUG: Suggestions:', response.suggestions);
            }
            
            // Process suggestions into interactive buttons
            let formattedText = response.text;
            
            // Remove the buttonHtml generation since we'll render suggestions separately
            formattedText = response.text;
            
            // Create AI message object
            const aiMessageObj = {
                id: generateMessageId(),
                content: formattedText,
                text: formattedText,
                sender: 'ai',
                timestamp: new Date(),
                suggestions: response.suggestions || []
            };
            
            // Add to UI
            addMessage(formattedText, 'ai', response.suggestions || []);
            
            // Wait for DOM update then attach button handlers
            setTimeout(attachButtonHandlers, 100);
            return;
        }
        
        // Safely extract bounded region with fallbacks for missing data
        const boundedRegion = {
            x: region?.x || 0,
            y: region?.y || 0,
            width: region?.width || 10,
            height: region?.height || 10
        };

        const prompt = `
            Question about chart region: ${question}

            Chart Description: ${description || 'No description available'}
            
            Selected Region:
            - Position: (${boundedRegion.x.toFixed(1)}%, ${boundedRegion.y.toFixed(1)}%)
            - Size: ${boundedRegion.width.toFixed(1)}% × ${boundedRegion.height.toFixed(1)}%
            
            Image Details:
            - Full Image URL: ${imageData.src || 'Not available'}
            - Dimensions: ${imageData.naturalWidth || 'unknown'}x${imageData.naturalHeight || 'unknown'}

            Please analyze this specific region of the chart and explain what it shows. Do not make up information. You are a data analyst and educator. Use informal ryan reynolds style.
            
            Format your response as JSON with a message and suggestions for next steps:
            {
              "message": "Your analysis of the chart region",
              "suggestions": [
                "Would you like to know more about this specific trend?",
                "Should we compare this to another part of the chart?",
                "Would you like to understand what might be causing this pattern?"
              ]
            }
        `;
        
        const response = await generateResponse(prompt, context);
        
        if (DEBUG_MODE) {
            console.log('DEBUG: Region response received:', response);
            console.log('DEBUG: Region suggestions:', response.suggestions);
        }
        
        // Process suggestions into interactive buttons
        let formattedText = response.text;
        
        // Remove the buttonHtml generation since we'll render suggestions separately
        formattedText = response.text;
        
        // Create AI message object
        const aiMessageObj = {
            id: generateMessageId(),
            content: formattedText,
            text: formattedText,
            sender: 'ai',
            timestamp: new Date(),
            suggestions: response.suggestions || []
        };
        
        // Add to UI
        addMessage(formattedText, 'ai', response.suggestions || []);
        
        // Wait for DOM update then attach button handlers
        setTimeout(attachButtonHandlers, 100);
    } catch (error) {
        console.error('Error in handleResponse:', error);
        messages = messages.map(msg => 
            msg.id === loadingId 
                ? { ...msg, text: "Sorry, I couldn't analyze this chart region. There might be an issue with the image data." }
                : msg
        );
        currentSuggestions = [];
    }
}

    // Function to attach click handlers to AI buttons
    function attachButtonHandlers() {
        // Find all AI suggestion buttons
        const buttons = document.querySelectorAll('.ai-suggestion-button:not(.custom-message-button)');
        
        console.log(`Found ${buttons.length} suggestion buttons to attach handlers to`);
        
        // Attach click handlers to each button
        buttons.forEach(button => {
            // Remove existing listeners to prevent duplicates
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
            
            // Add click event listener
            newButton.addEventListener('click', () => {
                const message = newButton.getAttribute('data-message');
                if (message) {
                    console.log(`Button clicked with message: ${message}`);
                    sendMessage(message);
                }
            });
        });
        
        // Handle custom message button separately
        const customButton = document.querySelector('.custom-message-button');
        if (customButton) {
            const newCustomButton = customButton.cloneNode(true);
            customButton.parentNode.replaceChild(newCustomButton, customButton);
            
            newCustomButton.addEventListener('click', () => {
                showCustomInput = true;
                // Force Svelte to update
                setTimeout(() => {
                    const inputElement = document.querySelector('.custom-input-container input');
                    if (inputElement) {
                        inputElement.focus();
                    }
                }, 100);
            });
        }
    }

    // Add error boundary
    let hasError = false;
    function handleError(error) {
        console.error('Chat component error:', error);
        hasError = true;
        messages = [...messages, {
            id: generateMessageId(),
            text: "Something went wrong. Please refresh the page.",
            sender: SENDER_AI
        }];
    }

    // Add debug logging
    $: {
        if (evidenceState?.currentEvidence) {
            console.log('Current evidence updated:', evidenceState.currentEvidence);
        }
    }

    $: {
        if (messages.length > 0) {
            console.log('Messages updated:', messages);
        }
    }

    // Add global function to handle AI button clicks
    onMount(() => {
        window.sendAIMessage = (message) => {
            if (typeof message === 'string' && message.trim()) {
                sendMessage(message);
            }
        };
    });
    
    onDestroy(() => {
        // Clean up global function
        window.sendAIMessage = undefined;
    });

    // Add function to toggle chat history visibility
    function toggleChatHistory() {
        isHistoryVisible = !isHistoryVisible;
        
        // If showing history, scroll to bottom after a brief delay
        if (isHistoryVisible) {
            setTimeout(() => {
                if (chatBox) {
                    chatBox.scrollTo({
                        top: chatBox.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    }

</script>
<div class="chat-container">
    <!-- Show connection error only after multiple failures -->
    {#if !connectionStatus.connected && connectionStatus.lastChecked && connectionFailures >= 2}
        <div class="connection-status-bar" class:firewall={connectionStatus.errorType === 'firewall'}>
            <div class="connection-icon">
                <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="connection-message">
                {#if connectionStatus.errorType === 'firewall'}
                    <strong>School Firewall Detected:</strong> The AI service appears to be blocked by your school network. Please inform your teacher.
                {:else if connectionStatus.errorType === 'network'}
                    <strong>Network Issue:</strong> Cannot connect to the AI service. Please check your internet connection.
                {:else if connectionStatus.errorType === 'auth'}
                    <strong>Authentication Issue:</strong> Cannot authenticate with the AI service.
                {:else}
                    <strong>Connection Problem:</strong> Cannot connect to AI service. {connectionStatus.error}
                {/if}
            </div>
            <button class="retry-connection" on:click={checkApiConnection}>
                {connectionStatus.checking ? 'Checking...' : 'Retry Connection'}
            </button>
        </div>
    {/if}

    <!-- New minimalist chat interface -->
    <div class="minimalist-chat" bind:this={chatBox}>
        <!-- Error and loading states -->
        {#if hasError}
            <div class="error-message">
                Something went wrong. Please refresh the page.
            </div>
        {/if}
      
        {#if errorMessage}
            <div class="error-message">
                {errorMessage}
                <button class="retry-button" on:click={() => { errorMessage = null; hasInitializedChat = false; initializeChatIfReady(); checkApiConnection(); }}>
                    Retry
                </button>
            </div>
        {/if}
        
        <!-- Show loading state when initializing -->
        {#if isInitializing && messages.length === 0}
            <div class="loading-container">
                <div class="loading-spinner"></div>
                <p>Initializing AI assistant...</p>
            </div>
        {:else if !authState?.user?.uid || !storyState?._id || !evidenceState?.currentEvidence?._id}
            <div class="loading-container">
                <div class="info-icon">
                    <i class="fas fa-info-circle"></i>
                </div>
                <p>AI assistant is waiting for required data to load. Please ensure you're logged in and have selected a piece of evidence.</p>
            </div>
        {:else if messages.length === 0 && !isInitializing}
            <div class="loading-container">
                <div class="loading-spinner"></div>
                <p>Loading chart assistant...</p>
            </div>
        {:else}
            <!-- Main chat view -->
            <div class="current-exchange">
                {#if messages.length > 0}
                    {#if !isHistoryVisible}
                        <!-- Show only latest exchange when history is not visible -->
                        {#if messages[messages.length - 1] && messages[messages.length - 1].sender === 'ai'}
                            <!-- Show last user message and AI response pair -->
                            {#if messages.length > 1}
                                <div 
                                    id={messages[messages.length - 2].id}
                                    class="message user"
                                >
                                    <div class="message-content">
                                        {messages[messages.length - 2].content}
                                    </div>
                                </div>
                            {/if}
                            <div 
                                id={messages[messages.length - 1].id}
                                class="message ai" 
                                class:loading={messages[messages.length - 1].text === 'Thinking...'}
                            >
                                <div class="message-content">
                                    {messages[messages.length - 1].content}
                                    {#if messages[messages.length - 1].suggestions && messages[messages.length - 1].suggestions.length > 0}
                                        <div class="ai-suggestion-buttons">
                                            {#each messages[messages.length - 1].suggestions as suggestion}
                                                <button 
                                                    class="ai-suggestion-button" 
                                                    data-message={suggestion}
                                                    on:click={() => sendMessage(suggestion)}
                                                >
                                                    {suggestion}
                                                </button>
                                            {/each}
                                            <button 
                                                class="ai-suggestion-button custom-message-button"
                                                on:click={() => showCustomInput = true}
                                            >
                                                Type your own message...
                                            </button>
                                            {#if showCustomInput}
                                                <div class="custom-input-container" transition:slide>
                                                    <input
                                                        type="text"
                                                        bind:value={userInput}
                                                        placeholder="Type your message..."
                                                        on:keydown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                                                    />
                                                    <button
                                                        class="send-custom-btn"
                                                        on:click={() => sendMessage()}
                                                        disabled={!userInput.trim()}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                                            <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            {/if}
                                        </div>
                                    {/if}
                                </div>
                            </div>
                        {:else if messages[messages.length - 1] && messages[messages.length - 1].sender === 'user'}
                            <!-- Just user message, waiting for AI response -->
                            <div 
                                id={messages[messages.length - 1].id}
                                class="message user"
                            >
                                <div class="message-content">
                                    {messages[messages.length - 1].content}
                                </div>
                            </div>
                            <div class="message ai loading">
                                <div class="message-content">
                                    Thinking...
                                </div>
                            </div>
                        {/if}
                    {:else}
                        <!-- Show full message history -->
                        {#each messages as message (message.id)}
                            <div 
                                id={message.id}
                                class="message {message.sender}" 
                                class:loading={message.text === 'Thinking...'}
                            >
                                <div class="message-content">
                                    {#if message.sender === 'ai'}
                                        {@html message.content}
                                        {#if message.id === messages[messages.length - 1].id && message.suggestions && message.suggestions.length > 0}
                                            <div class="ai-suggestion-buttons">
                                                {#each message.suggestions as suggestion}
                                                    <button 
                                                        class="ai-suggestion-button" 
                                                        data-message={suggestion}
                                                        on:click={() => sendMessage(suggestion)}
                                                    >
                                                        {suggestion}
                                                    </button>
                                                {/each}
                                                <button 
                                                    class="ai-suggestion-button custom-message-button"
                                                    on:click={() => showCustomInput = true}
                                                >
                                                    Type your own message...
                                                </button>
                                            </div>
                                        {/if}
                                    {:else}
                                        {message.content}
                                    {/if}
                                </div>
                            </div>
                        {/each}
                        {#if showCustomInput}
                            <div class="custom-input-container" transition:slide>
                                <input
                                    type="text"
                                    bind:value={userInput}
                                    placeholder="Type your message..."
                                    on:keydown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                                />
                                <button
                                    class="send-custom-btn"
                                    on:click={() => sendMessage()}
                                    disabled={!userInput.trim()}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                        <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                                    </svg>
                                </button>
                            </div>
                        {/if}
                    {/if}
                {/if}
            </div>

            <!-- History button - only show when there's more than one message -->
            {#if messages.length > 1}
              <div class="history-button-container">
                <button 
                  class="history-icon-button" 
                  on:click={toggleChatHistory} 
                  title={isHistoryVisible ? "Show recent messages" : "Show full conversation"}
                >
                  {#if isHistoryVisible}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M19,3H5C3.89,3 3,3.89 3,5V9H5V5H19V19H5V15H3V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M10.08,15.58L11.5,17L16.5,12L11.5,7L10.08,8.41L12.67,11H3V13H12.67L10.08,15.58Z" />
                    </svg>
                  {:else}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M13.5,8H12V13L16.28,15.54L17,14.33L13.5,12.25V8M13,3A9,9 0 0,0 4,12H1L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3" />
                    </svg>
                  {/if}
                </button>
              </div>
            {/if}
        {/if}
    </div>

    <!-- Enhanced Input controls -->
    <div class="chat-controls">
        <div class="input-wrapper">
            <!-- Remove quick reply buttons -->
            <div class="input-group">
                <input
                    type="text"
                    bind:value={userInput}
                    placeholder={isInitializing ? "Initializing..." : "Type your message..."}
                    on:keydown={(e) => e.key === 'Enter' && !e.shiftKey && !isInitializing && hasInitializedChat && sendMessage()}
                    disabled={isInitializing || !hasInitializedChat}
                />
                <button
                    class="settings-button"
                    on:click={toggleSettings}
                    title="AI Settings"
                    disabled={isInitializing}
                >
                    <!-- Replace FontAwesome with inline SVG -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                        <path fill="currentColor" d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
                    </svg>
                </button>
                <button
                    on:click={sendMessage}
                    class="send-btn"
                    disabled={!userInput.trim() || isInitializing || !hasInitializedChat}
                >
                    <!-- Replace FontAwesome with inline SVG -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                        <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</div>
  
<!-- Add this before the closing style tag -->
{#if showSettings}
    <div class="settings-modal" on:click|self={() => showSettings = false}>
        <div class="settings-content">
            <h3>AI Instructions</h3>
            <textarea
                bind:value={editableInstructions}
                rows="10"
                placeholder="Enter custom instructions for the AI..."
            ></textarea>
            <div class="settings-actions">
                <button class="reset-button" on:click={resetInstructions}>
                    Reset to Default
                </button>
                <button class="save-button" on:click={saveInstructions}>
                    Save Changes
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
    .chat-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        max-height: 100%;
        background-color: #f8fafc;
    }

    /* Connection status bar */
    .connection-status-bar {
        display: flex;
        align-items: center;
        padding: 0.75rem 1rem;
        background-color: #fef2f2;
        color: #b91c1c;
        gap: 0.75rem;
        margin: 0 1rem 1rem;
        border-radius: 6px;
    }

    .retry-connection {
        background-color: #ef4444;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 0.375rem 0.75rem;
        font-size: 0.75rem;
        cursor: pointer;
    }

    /* New minimalist chat interface */
    .minimalist-chat {
        flex: 1;
        display: flex;
        flex-direction: column;
        position: relative;
        margin: 0px;
        border-radius: 8px;
        background: #121212;
        overflow: hidden;
        min-height: 300px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease-in-out;
    }

    .current-exchange {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 2rem;
        padding-top: 3rem; /* Add extra padding at the top for the history button */
        overflow-y: auto;
        max-height: 100%;
        transition: all 0.3s ease-in-out;
    }

    .message {
        margin-bottom: 1.5rem;
        max-width: 90%;
        animation: fadeIn 0.3s ease forwards;
    }
    
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(5px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .message.user {
        align-self: flex-end;
    }

    .message.ai {
        align-self: flex-start;
    }

    .message-content {
        font-size: 1rem;
        line-height: 1.6;
        white-space: pre-wrap;
    }

    .message.user .message-content {
        color: #60A5FA; /* Light blue for user messages */
        font-weight: 500;
    }

    .message.ai .message-content {
        color: rgba(255, 255, 255, 0.85);
    }

    /* AI suggestion buttons styles */
    :global(.ai-suggestion-buttons) {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 16px;
        width: 100%;
    }

    :global(.ai-suggestion-button) {
        display: inline-block;
        padding: 8px 16px;
        background-color: rgba(96, 165, 250, 0.1);
        color: #60A5FA;
        border: 1px solid rgba(96, 165, 250, 0.3);
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: left;
        margin: 4px 0;
    }

    :global(.ai-suggestion-button:hover) {
        background-color: rgba(96, 165, 250, 0.2);
        border-color: rgba(96, 165, 250, 0.5);
    }

    :global(.ai-suggestion-button:active) {
        transform: scale(0.98);
    }

    .message.loading .message-content {
        color: rgba(255, 255, 255, 0.5);
        animation: pulse 1.5s infinite;
    }

    @keyframes pulse {
        0% { opacity: 0.5; }
        50% { opacity: 0.8; }
        100% { opacity: 0.5; }
    }

    /* History button and chat history */
    .history-button-container {
        position: absolute;
        top: 1rem;
        right: 1rem;
        display: flex;
        justify-content: flex-end;
        z-index: 10;
        pointer-events: none; /* Allow clicking through the container */
    }

    .history-icon-button {
        background: rgba(60, 60, 60, 0.5);
        border: none;
        color: rgba(255, 255, 255, 0.7);
        cursor: pointer;
        padding: 8px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        opacity: 0.8;
        pointer-events: auto; /* Ensure the button itself is clickable */
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
    
    .history-icon-button:hover {
        background: rgba(80, 80, 80, 0.7);
        color: rgba(255, 255, 255, 1);
        opacity: 1;
        transform: translateY(1px);
    }
    
    /* Responsive adjustments for the history button */
    @media (max-width: 640px) {
        .history-button-container {
            top: 0.5rem;
            right: 0.5rem;
        }
        
        .history-icon-button {
            padding: 6px;
        }
        
        .history-icon-button svg {
            width: 16px;
            height: 16px;
        }
        
        .current-exchange {
            padding-top: 3rem; /* Less padding on mobile */
        }
    }

    /* Error and loading states */
    .error-message {
        color: #ef4444;
        padding: 1rem;
        margin: 1rem;
        background-color: rgba(239, 68, 68, 0.1);
        border-radius: 8px;
        text-align: center;
    }
    
    .retry-button {
        margin-top: 0.5rem;
        padding: 0.375rem 0.75rem;
        background-color: #ef4444;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.875rem;
    }
    
    .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 1.5rem;
        text-align: center;
        color: rgba(255, 255, 255, 0.7);
        min-height: 200px;
    }

    .loading-container p {
        margin-top: 1rem;
        font-size: 0.875rem;
    }

    .loading-spinner {
        width: 32px;
        height: 32px;
        border: 2px solid rgba(255, 255, 255, 0.1);
        border-radius: 50%;
        border-top-color: rgba(255, 255, 255, 0.5);
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .info-icon {
        font-size: 24px;
        color: rgba(255, 255, 255, 0.5);
    }

    /* Input controls */
    .chat-controls {
        padding: 1rem;
        background: #f8fafc;
    }

    .input-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .input-group {
        display: flex;
        gap: 0.5rem;
        padding: 0.5rem;
        background-color: #fff;
        border-radius: 8px;
        align-items: center;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }

    .input-group input {
        flex: 1;
        padding: 0.75rem;
        border: none;
        background-color: transparent;
        font-size: 1rem;
        color: #1e293b;
    }

    .input-group input:focus {
        outline: none;
    }

    .input-group input::placeholder {
        color: #94a3b8;
    }

    .settings-button {
        border: none;
        background: none;
        color: #64748b;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .settings-button:hover {
        background: #f1f5f9;
        color: #3b82f6;
    }

    .send-btn {
        background-color: #000;
        color: white;
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .send-btn:hover {
        background-color: #1e1e1e;
    }

    .send-btn:disabled {
        background-color: #cbd5e1;
        cursor: not-allowed;
    }

    /* Message highlight animation */
    @keyframes highlightPulse {
        0% { background-color: rgba(96, 165, 250, 0); }
        50% { background-color: rgba(96, 165, 250, 0.2); }
        100% { background-color: rgba(96, 165, 250, 0); }
    }

    .highlight-message {
        animation: highlightPulse 2s ease-in-out;
        border-radius: 8px;
    }

    /* Settings modal */
    .settings-modal {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .settings-content {
        background: white;
        padding: 2rem;
        border-radius: 12px;
        width: 90%;
        max-width: 500px;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }

    .settings-content h3 {
        margin: 0 0 1rem 0;
        color: #1e293b;
        font-size: 1.25rem;
    }

    .settings-content textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        font-family: inherit;
        font-size: 0.95rem;
        line-height: 1.5;
        resize: vertical;
        margin-bottom: 1rem;
    }

    .settings-content textarea:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .settings-actions {
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
    }

    .settings-actions button {
        padding: 0.5rem 1rem;
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
    }

    .reset-button {
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
        color: #64748b;
    }

    .reset-button:hover {
        background: #e2e8f0;
        color: #1e293b;
    }

    .save-button {
        background: #3b82f6;
        border: 1px solid #3b82f6;
        color: white;
    }

    .save-button:hover {
        background: #2563eb;
    }

    /* Responsive adjustments */
    @media (max-width: 640px) {
        .minimalist-chat {
            margin: 0 0.5rem 0.5rem;
        }
        
        .current-exchange {
            padding: 1.5rem;
            padding-top: 4rem;
        }
        
        .chat-controls {
            padding: 0.5rem;
        }

        .input-group input {
            padding: 0.5rem;
            font-size: 0.75rem;
        }
        
        .history-icon-button {
            padding: 5px;
        }
        
        .history-icon-button svg {
            width: 16px;
            height: 16px;
        }
    }

    .custom-message-button {
        background-color: rgba(96, 165, 250, 0.05) !important;
        border-style: dashed !important;
    }

    .custom-input-container {
        display: flex;
        gap: 8px;
        width: 100%;
        margin-top: 8px;
        padding: 4px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 20px;
        align-items: center;
    }

    .custom-input-container input {
        flex: 1;
        background: transparent;
        border: none;
        padding: 8px 12px;
        color: #fff;
        font-size: 0.9rem;
    }

    .custom-input-container input:focus {
        outline: none;
    }

    .custom-input-container input::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }

    .send-custom-btn {
        background: #60A5FA;
        border: none;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: white;
        padding: 0;
        transition: all 0.2s ease;
    }

    .send-custom-btn:hover {
        background: #3B82F6;
        transform: scale(1.05);
    }

    .send-custom-btn:disabled {
        background: rgba(96, 165, 250, 0.3);
        cursor: not-allowed;
    }

    /* Remove the original input controls since we're moving them to suggestions */
    .chat-controls {
        display: none;
    }
</style>
  